/**
 * NEXUS UI - SegmentedControl
 * Tab-like selection control with animated indicator
 */

import React, { useRef, useEffect, useState } from 'react';
import type { BaseProps, NexusVariant, NexusSize } from './types';

interface Segment {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps extends BaseProps {
  segments: Segment[];
  value: string;
  onChange: (value: string) => void;
  variant?: NexusVariant;
  size?: NexusSize;
  fullWidth?: boolean;
  disabled?: boolean;
}

const variantColors: Record<NexusVariant, { base: string; glow: string }> = {
  cyan: { base: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { base: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { base: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { base: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { base: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { base: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

const sizeConfig: Record<NexusSize, { padding: string; fontSize: string; height: number }> = {
  sm: { padding: '6px 12px', fontSize: '11px', height: 28 },
  md: { padding: '8px 16px', fontSize: '12px', height: 36 },
  lg: { padding: '10px 20px', fontSize: '13px', height: 44 },
  xl: { padding: '12px 24px', fontSize: '14px', height: 52 },
};

export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  segments,
  value,
  onChange,
  variant = 'cyan',
  size = 'md',
  fullWidth = false,
  disabled = false,
  className = '',
  style,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const colors = variantColors[variant];
  const config = sizeConfig[size];

  // Update indicator position when value changes
  useEffect(() => {
    if (!containerRef.current) return;

    const activeIndex = segments.findIndex(s => s.value === value);
    if (activeIndex === -1) return;

    const buttons = containerRef.current.querySelectorAll('button');
    const activeButton = buttons[activeIndex] as HTMLElement;

    if (activeButton) {
      setIndicatorStyle({
        left: activeButton.offsetLeft,
        width: activeButton.offsetWidth,
      });
    }
  }, [value, segments]);

  return (
    <div
      ref={containerRef}
      className={`nx-segmented-control ${className}`}
      role="tablist"
      style={{
        position: 'relative',
        display: 'inline-flex',
        background: 'var(--nx-void-elevated)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '6px',
        padding: '3px',
        opacity: disabled ? 0.5 : 1,
        width: fullWidth ? '100%' : 'auto',
        ...style,
      }}
    >
      {/* Animated indicator */}
      <div
        style={{
          position: 'absolute',
          top: 3,
          left: indicatorStyle.left,
          width: indicatorStyle.width,
          height: `calc(100% - 6px)`,
          background: `linear-gradient(135deg, ${colors.base}20, ${colors.base}10)`,
          border: `1px solid ${colors.base}`,
          borderRadius: '4px',
          boxShadow: `0 0 12px ${colors.glow}, inset 0 0 8px ${colors.glow}`,
          transition: 'left 0.2s ease-out, width 0.2s ease-out',
          pointerEvents: 'none',
        }}
      />

      {segments.map((segment) => {
        const isActive = segment.value === value;
        const isDisabled = disabled || segment.disabled;

        return (
          <button
            key={segment.value}
            role="tab"
            aria-selected={isActive}
            disabled={isDisabled}
            onClick={() => !isDisabled && onChange(segment.value)}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: config.padding,
              minHeight: config.height,
              background: 'transparent',
              border: 'none',
              borderRadius: '4px',
              fontFamily: 'var(--nx-font-interface)',
              fontSize: config.fontSize,
              fontWeight: 500,
              color: isActive ? colors.base : 'var(--nx-text-secondary)',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              transition: 'color 0.15s ease-out',
              flex: fullWidth ? 1 : 'none',
              whiteSpace: 'nowrap',
              zIndex: 1,
            }}
            onMouseOver={(e) => {
              if (!isActive && !isDisabled) {
                e.currentTarget.style.color = 'var(--nx-text-primary)';
              }
            }}
            onMouseOut={(e) => {
              if (!isActive && !isDisabled) {
                e.currentTarget.style.color = 'var(--nx-text-secondary)';
              }
            }}
          >
            {segment.icon && (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  opacity: isActive ? 1 : 0.7,
                }}
              >
                {segment.icon}
              </span>
            )}
            <span>{segment.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
